/**
 * PlaybackTimingController - Centralized timing control for audio-canvas synchronization
 * Ensures audio and canvas remain in sync during playback
 */
export class PlaybackTimingController {
  private static instance: PlaybackTimingController;
  
  private audioTime: number = 0;
  private canvasTime: number = 0;
  private lastSyncTime: number = 0;
  private syncThreshold: number = 50; // 50ms tolerance before forcing sync
  private driftCorrection: number = 0;
  private syncCallbacks: Set<(time: number) => void> = new Set();
  private rafId: number | null = null;
  private isPlaying: boolean = false;
  
  // Performance monitoring
  private frameDrops: number = 0;
  private lastFrameTime: number = 0;
  private targetFPS: number = 60;
  private frameInterval: number = 1000 / this.targetFPS;
  
  private constructor() {}
  
  static getInstance(): PlaybackTimingController {
    if (!PlaybackTimingController.instance) {
      PlaybackTimingController.instance = new PlaybackTimingController();
    }
    return PlaybackTimingController.instance;
  }
  
  /**
   * Register a callback to be called on each sync update
   */
  registerSyncCallback(callback: (time: number) => void): () => void {
    this.syncCallbacks.add(callback);
    return () => {
      this.syncCallbacks.delete(callback);
    };
  }
  
  /**
   * Update audio time from audio element
   */
  updateAudioTime(time: number): void {
    const previousAudioTime = this.audioTime;
    this.audioTime = time;
    
    // Calculate drift between audio and canvas
    const drift = Math.abs(this.canvasTime - this.audioTime);
    
    // If drift exceeds threshold, force sync
    if (drift > this.syncThreshold) {
      console.warn(`Audio-Canvas drift detected: ${drift}ms. Forcing sync.`);
      this.forceSync();
    } else {
      // Smooth drift correction for small differences
      this.driftCorrection = (this.audioTime - this.canvasTime) * 0.1;
    }
    
    // Track time jumps (seeking)
    if (Math.abs(time - previousAudioTime) > 100) {
      this.handleSeek(time);
    }
  }
  
  /**
   * Update canvas time
   */
  updateCanvasTime(time: number): void {
    this.canvasTime = time;
  }
  
  /**
   * Get synchronized time (with drift correction)
   */
  getSyncedTime(): number {
    // Return audio time as the source of truth
    return this.audioTime;
  }
  
  /**
   * Force canvas to sync with audio
   */
  private forceSync(): void {
    this.canvasTime = this.audioTime;
    this.driftCorrection = 0;
    this.notifySync(this.audioTime);
  }
  
  /**
   * Handle seek events
   */
  private handleSeek(newTime: number): void {
    this.audioTime = newTime;
    this.canvasTime = newTime;
    this.driftCorrection = 0;
    this.notifySync(newTime);
  }
  
  /**
   * Notify all registered callbacks
   */
  private notifySync(time: number): void {
    this.syncCallbacks.forEach(callback => {
      try {
        callback(time);
      } catch (error) {
        console.error('Sync callback error:', error);
      }
    });
  }
  
  /**
   * Start the synchronization loop
   */
  startSyncLoop(audioElement: HTMLAudioElement | null): void {
    if (this.rafId !== null) {
      this.stopSyncLoop();
    }
    
    this.isPlaying = true;
    this.lastFrameTime = performance.now();
    
    const syncLoop = () => {
      if (!this.isPlaying) return;
      
      const now = performance.now();
      const elapsed = now - this.lastFrameTime;
      
      // Frame rate limiting for consistent updates
      if (elapsed >= this.frameInterval) {
        // Update timing from audio if available
        if (audioElement && !audioElement.paused && !audioElement.ended) {
          const audioTime = audioElement.currentTime * 1000;
          this.updateAudioTime(audioTime);
        }
        
        // Apply drift correction to canvas time
        if (this.driftCorrection !== 0) {
          this.canvasTime += this.driftCorrection;
          this.updateCanvasTime(this.canvasTime);
        }
        
        // Notify all listeners with synced time
        this.notifySync(this.getSyncedTime());
        
        // Track frame drops
        if (elapsed > this.frameInterval * 2) {
          this.frameDrops++;
          if (this.frameDrops % 100 === 0) {
            console.warn(`Frame drops detected: ${this.frameDrops}`);
          }
        }
        
        this.lastFrameTime = now - (elapsed % this.frameInterval);
      }
      
      this.rafId = requestAnimationFrame(syncLoop);
    };
    
    this.rafId = requestAnimationFrame(syncLoop);
  }
  
  /**
   * Stop the synchronization loop
   */
  stopSyncLoop(): void {
    this.isPlaying = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }
  
  /**
   * Reset the controller
   */
  reset(): void {
    this.stopSyncLoop();
    this.audioTime = 0;
    this.canvasTime = 0;
    this.driftCorrection = 0;
    this.frameDrops = 0;
    this.lastSyncTime = 0;
    this.syncCallbacks.clear();
  }
  
  /**
   * Get diagnostics information
   */
  getDiagnostics(): {
    audioTime: number;
    canvasTime: number;
    drift: number;
    frameDrops: number;
    isPlaying: boolean;
  } {
    return {
      audioTime: this.audioTime,
      canvasTime: this.canvasTime,
      drift: Math.abs(this.audioTime - this.canvasTime),
      frameDrops: this.frameDrops,
      isPlaying: this.isPlaying
    };
  }
}